'use client'

import { useCallback } from 'react'

import { lowestPlanWhere } from '@/lib/billing/capabilities'
import { usePlans } from '@/service/billing.hook'
import { Capabilities } from '@/types/generated/public_api'

/**
 * Resolves the label of the cheapest plan that satisfies a capability predicate,
 * for "Requires <plan>" upsell hints on locked controls. Driven by the public
 * `get_plans` catalog so it never hardcodes the tier table. Returns undefined
 * while the catalog loads or when no plan satisfies the predicate — fall back to
 * a generic upgrade prompt.
 */
export function useUpgradeHint(): (
  predicate: (caps: Capabilities) => boolean
) => string | undefined {
  const { data } = usePlans()
  const plans = data?.plans

  return useCallback(
    (predicate: (caps: Capabilities) => boolean) =>
      // Coerce a missing/empty label (ts-proto defaults `label` to '') to
      // undefined so "did a plan resolve?" reads the same for callers that test
      // `!== undefined` and those that test truthiness.
      lowestPlanWhere(plans ?? [], predicate)?.label || undefined,
    [plans]
  )
}
